import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/site/Logo";
import { useLeadDialog } from "@/components/site/LeadDialog";
import { ArrowUpRight, Menu, X } from "lucide-react";

const NAV = [
  { label: "Work", href: "#work" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Contact", href: "#contact" },
];

export default function Header({ mode = "auto" }) {
  const { openPreview } = useLeadDialog();
  const [scrolled, setScrolled] = useState(false);
  const [dark, setDark] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 16);
      // Auto-detect background at header line - a lightweight heuristic:
      const el = document.elementFromPoint(window.innerWidth / 2, 8);
      const bg = el ? getComputedStyle(el).backgroundColor : "";
      // We flag dark if computed body/section CSS class contains dark data attr
      const inDark = !!document
        .elementFromPoint(window.innerWidth / 2, 40)
        ?.closest("[data-theme='dark']");
      setDark(inDark);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isDark = mode === "dark" || (mode === "auto" && dark);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? isDark
            ? "bg-black/50 backdrop-blur-xl border-b border-white/5"
            : "bg-[color:var(--ww-cream)]/70 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[92rem] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Logo dark={isDark} />

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={`text-[13px] tracking-tight font-medium transition-colors ${
                isDark
                  ? "text-white/60 hover:text-white"
                  : "text-black/55 hover:text-black"
              }`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => openPreview()}
            data-testid="header-cta-free-preview"
            data-magnetic
            className={`hidden sm:inline-flex items-center gap-2 pl-4 pr-3 py-2 text-[13px] font-medium rounded-full border transition-all ${
              isDark
                ? "border-white/20 text-white hover:bg-white/10"
                : "border-black/15 text-black hover:bg-black hover:text-white"
            }`}
          >
            Free Website Preview
            <span
              className={`inline-grid place-items-center w-6 h-6 rounded-full transition-colors ${
                isDark
                  ? "bg-gradient-to-br from-teal-400 to-emerald-500 text-black"
                  : "bg-gradient-to-br from-teal-500 to-emerald-500 text-white"
              }`}
            >
              <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </button>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className={`lg:hidden inline-grid place-items-center w-10 h-10 rounded-full border ${
              isDark
                ? "border-white/15 text-white"
                : "border-black/10 text-black"
            }`}
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className={`lg:hidden border-t ${
              isDark
                ? "bg-black/80 backdrop-blur-xl border-white/5"
                : "bg-[color:var(--ww-cream)]/95 backdrop-blur-xl border-black/5"
            }`}
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  className={`text-base font-medium ${
                    isDark ? "text-white/85" : "text-black/85"
                  }`}
                >
                  {n.label}
                </a>
              ))}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  openPreview();
                }}
                data-testid="mobile-cta-free-preview"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-semibold bg-gradient-to-r from-teal-500 to-emerald-500 text-black"
              >
                Get Your Free Website Preview
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
